import { useCallback, useEffect, useState } from 'react';

import type { SettingsUser } from '@nightshift/ui-types';

import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import { api, type SessionUser } from '../live/api.ts';
import { useStore } from '../live/context.tsx';
import { canWriteSettings } from '../lib/privilege.ts';

export function UsersPanel({ user }: { user: SessionUser | null }) {
  const store = useStore();
  const canWrite = canWriteSettings(user);
  const [users, setUsers] = useState<SettingsUser[] | null>(null);
  const [membershipSyncedAt, setMembershipSyncedAt] = useState<number | null>(null);
  const [resyncing, setResyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const listed = await api.users();
      setUsers(listed.users);
      setMembershipSyncedAt(listed.membershipSyncedAt ?? null);
      setError(null);
    } catch (caught) {
      setUsers(null);
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!canWrite && user?.role === 'member') {
    return (
      <Empty
        title="User management is admin-only."
        body="Members can use the rest of Settings. Ask an admin to add, disable, or change accounts."
      />
    );
  }

  if (error && !users) {
    return (
      <Empty
        title="Users could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!users) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
        <Spinner size={20} />
      </div>
    );
  }

  const enabledAdmins = users.filter((row) => row.role === 'admin' && !row.disabled).length;

  const resync = async () => {
    setResyncing(true);
    try {
      const result = await api.resyncMembers();
      setMembershipSyncedAt(result.membershipSyncedAt);
      store.toast(
        result.ok ? 'good' : 'bad',
        result.ok
          ? `Membership refreshed for ${result.synced} project${result.synced === 1 ? '' : 's'}.`
          : `Refreshed ${result.synced}; failed: ${result.failed.map((row) => row.slug).join(', ')}.`,
      );
      await load();
    } catch (caught) {
      store.toast('bad', caught instanceof Error ? caught.message : String(caught));
    } finally {
      setResyncing(false);
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      await api.createUser({ username, displayName, role, password });
      setUsername('');
      setDisplayName('');
      setPassword('');
      setRole('member');
      store.toast('good', 'User created.');
      await load();
    } catch (caught) {
      store.toast('bad', caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="users-panel">
      <p className="sheet-note cfg-provenance">
        Accounts live in the database. The add-user CLI remains for bootstrap. A disabled user
        cannot log in, and an existing session cookie stops working immediately. The last enabled
        admin cannot be disabled or demoted.
      </p>
      <div className="sheet-note cfg-provenance">
        <p>
          Which projects a member sees is decided in GitLab, not here: they see a project when
          their linked GitLab account can read it. Admins see every project. A member with no
          GitLab account linked sees an empty board.{' '}
          {membershipSyncedAt === null
            ? 'Membership has not been read from GitLab yet.'
            : `Membership as of ${new Date(membershipSyncedAt).toLocaleString()}.`}
        </p>
        {canWrite ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={resyncing}
            onClick={() => void resync()}
            style={{ marginTop: 'var(--s2)' }}
          >
            {resyncing ? 'Refreshing…' : 'Refresh from GitLab'}
          </button>
        ) : null}
      </div>
      <section className="sheet-panel">
        <header className="panel-head">
          <Kicker count={users.length}>Users</Kicker>
        </header>
        <div className="sheet-panel-body">
          {users.map((row) => (
            <UserRow
              key={row.id}
              row={row}
              selfId={user?.id ?? null}
              enabledAdmins={enabledAdmins}
              canWrite={canWrite}
              busy={busy}
              onChanged={async (message) => {
                store.toast('good', message);
                await load();
              }}
              onError={(message) => store.toast('bad', message)}
            />
          ))}
        </div>
      </section>
      {canWrite ? (
        <section className="sheet-panel">
          <header className="panel-head">
            <Kicker>Add user</Kicker>
          </header>
          <div className="sheet-panel-body">
            <div className="cfg-grid">
              <label className="cfg-field">
                <span className="cfg-label">Username</span>
                <input className="input" value={username} disabled={busy} onChange={(e) => setUsername(e.target.value)} />
              </label>
              <label className="cfg-field">
                <span className="cfg-label">Display name</span>
                <input
                  className="input"
                  value={displayName}
                  disabled={busy}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </label>
              <label className="cfg-field">
                <span className="cfg-label">Role</span>
                <div className="seg">
                  {(['member', 'admin'] as const).map((entry) => (
                    <button
                      key={entry}
                      type="button"
                      aria-pressed={role === entry}
                      onClick={() => setRole(entry)}
                    >
                      {entry}
                    </button>
                  ))}
                </div>
              </label>
              <label className="cfg-field">
                <span className="cfg-label">Password</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  disabled={busy}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
            </div>
            <button
              type="button"
              className="btn btn-accent btn-sm"
              disabled={busy || username.trim().length === 0 || displayName.trim().length === 0 || password.length === 0}
              onClick={() => void create()}
            >
              Create
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function UserRow({
  row,
  selfId,
  enabledAdmins,
  canWrite,
  busy,
  onChanged,
  onError,
}: {
  row: SettingsUser;
  selfId: number | null;
  enabledAdmins: number;
  canWrite: boolean;
  busy: boolean;
  onChanged: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [password, setPassword] = useState('');
  const [gitlabDraft, setGitlabDraft] = useState(
    row.gitlabUserId === null ? '' : String(row.gitlabUserId),
  );
  const lastAdmin = row.role === 'admin' && !row.disabled && enabledAdmins <= 1;
  const protect = lastAdmin;

  const linkGitlab = async () => {
    const trimmed = gitlabDraft.trim();
    const value = trimmed === '' ? null : Number(trimmed);
    if (value !== null && (!Number.isInteger(value) || value <= 0)) {
      onError('GitLab user id must be a positive whole number.');
      return;
    }
    try {
      await api.patchUser(row.id, { gitlabUserId: value });
      await onChanged(
        value === null
          ? `${row.username} unlinked from GitLab; they now see no projects.`
          : `${row.username} linked to GitLab user ${value}.`,
      );
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  const patch = async (body: { role?: 'admin' | 'member'; disabled?: boolean }) => {
    try {
      await api.patchUser(row.id, body);
      const message =
        body.disabled === true
          ? `${row.username} disabled.`
          : body.disabled === false
            ? `${row.username} enabled.`
            : `${row.username} updated.`;
      await onChanged(message);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  const reset = async () => {
    if (password.length === 0) return;
    try {
      await api.resetUserPassword(row.id, password);
      setPassword('');
      await onChanged(`Password reset for ${row.username}.`);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  return (
    <div className="cfg-field" data-user={row.username}>
      <div style={{ minWidth: 0 }}>
        <span className="cfg-label">
          {row.displayName}{' '}
          <code className="cfg-path mono">{row.username}</code>
        </span>
        <p className="cfg-help">
          {row.role}
          {row.disabled ? ' · disabled' : ''}
          {selfId === row.id ? ' · you' : ''}
          {protect ? ' · last admin' : ''}
          {row.gitlabUserId === null
            ? row.role === 'admin'
              ? ' · no GitLab link (admins see everything)'
              : ' · no GitLab link, sees nothing'
            : ` · GitLab user ${row.gitlabUserId}`}
        </p>
      </div>
      {canWrite ? (
        <div className="cfg-control" style={{ flexWrap: 'wrap' }}>
          <div className="seg">
            {(['member', 'admin'] as const).map((entry) => (
              <button
                key={entry}
                type="button"
                aria-pressed={row.role === entry}
                disabled={busy || (protect && entry !== 'admin')}
                onClick={() => void patch({ role: entry })}
              >
                {entry}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy || (protect && !row.disabled)}
            onClick={() => void patch({ disabled: !row.disabled })}
          >
            {row.disabled ? 'Enable' : 'Disable'}
          </button>
          <input
            className="input"
            inputMode="numeric"
            placeholder="GitLab id"
            value={gitlabDraft}
            disabled={busy}
            onChange={(e) => setGitlabDraft(e.target.value)}
            style={{ maxWidth: 100 }}
            aria-label={`GitLab user id for ${row.username}`}
          />
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy || gitlabDraft.trim() === (row.gitlabUserId === null ? '' : String(row.gitlabUserId))}
            onClick={() => void linkGitlab()}
          >
            Link
          </button>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            placeholder="New password"
            value={password}
            disabled={busy}
            onChange={(e) => setPassword(e.target.value)}
            style={{ maxWidth: 140 }}
            aria-label={`New password for ${row.username}`}
          />
          <button type="button" className="btn btn-sm" disabled={busy || password.length === 0} onClick={() => void reset()}>
            Reset
          </button>
        </div>
      ) : null}
    </div>
  );
}
